import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/ContactForm';
import CTABanner from '../components/CTABanner';
import { Truck, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const roles = [
  'Class 1 & Class 3 Drivers',
  'Delivery Drivers',
  'Dispatchers',
  'Loading Dock Workers',
  'Logistics Coordinators',
  'Fleet Support Personnel',
  'Moving & Relocation Workers',
];

const benefits = [
  'Licensed and experienced drivers available',
  'Background-checked and safety-screened',
  'Flexible scheduling for routes and shifts',
  'Full WCB and insurance coverage',
  'Payroll administration included',
  'Fast placement for urgent needs',
];

const TransportationPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Transportation Staffing BC - WorkNation Labour Ltd | Drivers & Logistics Workers</title>
        <meta name="description" content="WorkNation Labour Ltd provides transportation staffing across BC. Class 1 & 3 drivers, delivery drivers, dock workers, dispatchers. Licensed and insured. Contact us for fast placement." />
        <link rel="canonical" href="https://worknation.ca/industries/transportation" />
      </Helmet>
      <Header />
      <main>
        <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center mb-4">
              <Truck className="text-indigo-600 mr-3" size={36} aria-hidden="true" />
              <span className="text-indigo-600 font-semibold text-lg uppercase tracking-wide">Industry Spotlight</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#003366] mb-6">Transportation Staffing in British Columbia</h1>
            <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mb-8">
              WorkNation Labour Ltd keeps BC&apos;s supply chain moving with reliable transportation and logistics personnel. From Class 1 drivers to loading dock workers, we provide the workforce that keeps goods flowing across British Columbia and beyond.
            </p>
            <Button onClick={() => navigate('/employers')} className="bg-[#003366] hover:bg-[#004488] text-white px-8 py-6 text-lg" data-testid="transportation-cta">
              Get Transportation Workers Now <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Transportation Roles We Fill</h2>
              <ul className="space-y-3">
                {roles.map((role, i) => (
                  <li key={i} className="flex items-center text-gray-700">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} aria-hidden="true" />
                    {role}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Why Employers Choose Us</h2>
              <ul className="space-y-3">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start text-gray-700">
                    <CheckCircle className="text-[#003366] mr-3 flex-shrink-0 mt-0.5" size={20} aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-[#003366] mb-6">Transportation Staffing Across BC</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              British Columbia is a critical link in Canada&apos;s transportation network, with the Port of Vancouver handling billions of dollars in trade annually. From long-haul trucking to last-mile delivery, the demand for qualified transportation workers continues to grow.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              WorkNation Labour Ltd supplies licensed, experienced drivers and logistics support personnel to transportation companies across the province. All drivers are background-checked, hold valid licences, and meet all regulatory requirements.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Whether you need a driver for a single route or an entire fleet support team, WorkNation delivers the workforce you need, when you need it.
            </p>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#003366] mb-6 text-center">Request Transportation Workers</h2>
            <ContactForm type="employer" />
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default TransportationPage;
